import { MultipartFailureKind } from '@atlas/types'
import { isAxiosError } from 'axios'
import { isNoSuchUploadResponse, MultipartUploadError } from './multipart-errors'
import type { PartPlan, PartProgress } from './multipart-parts'
import type { PartUrlProvider } from './multipart-presign'

/**
 * How many parts of ONE file transfer at a time.
 *
 * A part-level bound, unrelated to the upload manager's `MAX_CONCURRENT_UPLOADS`
 * budget, which counts files in its own queue. Nothing elsewhere fixes
 * part-level parallelism; this is the local ceiling that keeps one large file
 * from opening an unbounded number of connections.
 */
export const MULTIPART_PART_CONCURRENCY = 4

/** Attempts per part, independent of every other part — one failing part never restarts a successful one. */
export const MULTIPART_PART_MAX_ATTEMPTS = 3

/** Thrown when S3 reports the multipart upload itself is gone: the file's parts stop, and state is reconciled through `GET`. */
export class MultipartUploadGoneError extends Error {
	constructor() {
		super('The multipart upload no longer exists')
		this.name = 'MultipartUploadGoneError'
	}
}

export interface PartTransferDeps {
	file: Blob
	plan: Map<number, PartPlan>
	urls: PartUrlProvider
	uploadPart: (
		uploadUrl: string,
		body: Blob,
		options?: { signal?: AbortSignal; onBytes?: (loaded: number) => void },
	) => Promise<{ etag: string | null }>
	progress: PartProgress
	onProgress: (percent: number) => void
	signal?: AbortSignal
	concurrency?: number
}

/**
 * Transfers the given parts browser-to-S3 with bounded parallelism and
 * independent per-part retry, and returns the ETag S3 reported for each.
 *
 * Those ETags are diagnostic only: completion sends none, because Atlas reads
 * S3's own `ListParts` and never accepts client-supplied part state. They
 * are collected because a cross-origin response that does expose the header
 * makes a transferred part identifiable in a log.
 */
export async function transferParts(remaining: number[], deps: PartTransferDeps): Promise<Map<number, string | null>> {
	const queue = [...remaining].sort((a, b) => a - b)
	const etags = new Map<number, string | null>()
	const workers = Math.min(deps.concurrency ?? MULTIPART_PART_CONCURRENCY, Math.max(1, queue.length))

	await Promise.all(
		Array.from({ length: workers }, async () => {
			for (;;) {
				assertNotAborted(deps.signal)
				const partNumber = queue.shift()
				if (partNumber === undefined) return
				etags.set(partNumber, await transferOnePart(partNumber, queue, deps))
			}
		}),
	)
	return etags
}

/** One part, retried on its own. Every outcome that is not "this part can succeed later" leaves immediately. */
async function transferOnePart(partNumber: number, upcoming: number[], deps: PartTransferDeps): Promise<string | null> {
	const part = deps.plan.get(partNumber)
	if (!part) throw new Error(`No plan for part ${partNumber}`)
	const body = deps.file.slice(part.start, part.end)

	let lastError: unknown
	for (let attempt = 1; attempt <= MULTIPART_PART_MAX_ATTEMPTS; attempt++) {
		assertNotAborted(deps.signal)
		try {
			const uploadUrl = await deps.urls.urlFor(partNumber, upcoming)
			deps.urls.consume(partNumber)
			const { etag } = await deps.uploadPart(uploadUrl, body, {
				signal: deps.signal,
				onBytes: (loaded) => {
					deps.progress.trackInFlight(partNumber, Math.min(loaded, part.size))
					deps.onProgress(deps.progress.percent())
				},
			})
			deps.progress.markCompleted(partNumber, part.size)
			deps.onProgress(deps.progress.percent())
			return etag
		} catch (error) {
			assertNotAborted(deps.signal)
			// The upload id is gone: no URL for this file can ever work again, so
			// re-presigning would be a loop. Reconciliation through GET decides.
			if (isNoSuchUploadResponse(error)) throw new MultipartUploadGoneError()
			// A signature the bucket refused: drop the held URL so the retry signs a
			// new one rather than replaying the rejected signature.
			deps.urls.discard(partNumber)
			deps.progress.invalidate(partNumber)
			deps.onProgress(deps.progress.percent())
			if (isUnauthorizedTwice(error, attempt)) {
				throw new MultipartUploadError(
					'The upload was refused by storage. Please try again.',
					MultipartFailureKind.RESTART_REQUIRED,
					{},
				)
			}
			lastError = error
		}
	}
	throw lastError
}

/**
 * One defensive retry is allowed for a `403` that cannot be attributed to
 * expiry; a second one on a freshly signed URL is an authorization or
 * configuration failure, not a transient one, and retrying it further is
 * pointless.
 */
function isUnauthorizedTwice(error: unknown, attempt: number): boolean {
	return attempt >= 2 && isAxiosError(error) && error.response?.status === 403
}

function assertNotAborted(signal?: AbortSignal): void {
	if (signal?.aborted) throw new MultipartUploadError('Upload cancelled', MultipartFailureKind.CANCELLED)
}
